/*
MIT License

Copyright (c) 2023 Artem Churyukin

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

import {setImmediate,wait} from './psl_polyfills.js';
import psl_actions from './psl_actions.js';
import psl_templates from './psl_templates.js'
import psl_events from './psl_events.js';

const _templates=new Map();

const _render_container=document.createElement('div');
const _render_container_table=document.createElement('tbody');

function _add_helper(name,func){
	psl_templates.helpers.add(name,func);
}

async function _add_template(name,template){
	template=psl_templates.templates.add(name,template);
	_templates.set(name,template);
	const res=_render_template.bind(this, template);
	res.template_name=name;
	return res;
}

const _uid=new Uint32Array(2);

function _render_template(name,data={},rpoints_configs){
	window.crypto.getRandomValues(_uid);
	data.psl_ui_id='psl_ui_id_'+_uid[0]+'_'+_uid[1];
	const str_html=name.constructor===Function?name(data):_templates.get(name)(data);
	let container;
	if(str_html.substr(0,3)==='<tr'){
		container=_render_container_table;
	} else {
		container=_render_container;
	}
	container.innerHTML=str_html;
	let rpoints=_get_rpoints(container,rpoints_configs);
	let els=Array.from(container.childNodes);
	for(let i=els.length;i--;) if(els[i].nodeName==='#text') els.splice(i,1);
	delete data.psl_ui_id;
	let res={els,rpoints,name:name.template_name||name};
	if(els.length===1) res.el=els[0];
	return res;
}

function _resolve_path(rpoints,rpoint,el){
	rpoint=rpoint.split('.');
	if(rpoint.length===1) return rpoints[rpoint[0]]=el;
	let target=rpoints;
	for(let i=0;i<rpoint.length-1;i++){
		if(!target[rpoint[i]]) target[rpoint[i]]={};
		target=target[rpoint[i]];
	}
	target[rpoint[rpoint.length-1]]=el;
}

function _insert_template(config){
	//config.template - _render_template data,
	//config.target - {view,before/after};
	let template=_render_template(...config.template);
	for(let [name,node] of Object.entries(template.rpoints)) config.target.view.rpoints[name]=node;
	config.target.parent=(config.target.after?config.target.after.parentNode:undefined)||(config.target.before?config.target.before.parentNode:undefined);
	if(config.target.after) config.target.before=config.target.after.nextElementSibling;
	template.els.forEach(el=>{
		if(config.target.before){
			config.target.parent.insertBefore(el,config.target.before);
		} else {
			config.target.parent.appendChild(el);
		}
	});
}

function _get_rpoints(node,configs={}){
	let els=Array.from(node.querySelectorAll('[data-rpoint]'));
	let rpoints={};
	for(let el of els){
		let rpoint=el.dataset.rpoint;
		if(el.dataset.psl_ui&&_classes[el.dataset.psl_ui]) el=new _classes[el.dataset.psl_ui](el,configs[rpoint]);
		_resolve_path(rpoints,rpoint,el);
		delete el.dataset.rpoint;
		delete el.dataset.psl_ui;
	}
	return rpoints;
}

_add_template('psl_ui.input',
`<div class='input' data-psl_ui='input'{{#if name}} data-psl_ui_form_name='{{name}}'{{/if}}{{#if rpoint}} data-rpoint='{{rpoint}}'{{/if}}>
	{{#if label}}<label>{{label}}</label>{{/if}}
	<{{#if type === 'textarea'}}textarea{{else}}input type='{{type}}'{{/if}}
		{{#if placeholder}} placeholder='{{placeholder}}'{{/if}}
		{{#if autoresize}} autoresize{{/if}}
		{{#if oneline}} oneline{{/if}}
		{{#if tabs}} tabs{{/if}}
		{{#if readonly}} readonly{{/if}}
		{{#if input_rpoint}} data-rpoint='{{input_rpoint}}'{{/if}}
	></{{#if type === 'textarea'}}textarea{{else}}input{{/if}}>
	{{#if help}}<a class='help' data-action='{{help_action}}'{{#if help_data}} data-data='{{help_data}}'{{/if}}>{{help}}</a>{{/if}}
	<span class='error'></span>
</div>`);
_add_template('psl_ui.menu',
`<ul class="psl_ui_menu hidden">
	{{#each list}}
		<li data-action="{{action}}" data-data="{{data}}" class="{{#if icon}}icon{{/if}}">{{#if icon}}<i class="{{icon}}"></i>{{/if}}{{label}}</li>
	{{/each}}
</ul>
`);
_add_template('psl_ui.modal',`
<div class="psl_ui_modal hidden">
	<div class="container">
		<div class="content{{cls}}" data-rpoint="content">
			{{#if header}}
				<div class="header">
					<a class="close" data-action="{{#if close_action}}{{close_action}}{{else}}psl_ui.modal.close{{/if}}"><i class="zwicon-close"></i></a>
					<h5>{{title}}</h5>
				</div>
			{{/if}}
			<div class="body" data-rpoint="body">
				{{body}}
			</div>
			{{#if footer}}
				<div class="footer">
					{{#each actions}}
						<a class="{{cls}}" data-action="{{action}}"{{#if data}} data-data="{{data}}"{{/if}}>{{label}}</a>
					{{/each}}
				</div>
			{{/if}}
		</div>
	</div>
</div>
`);

const _classes={
	input:class{
		constructor(el){
			this._el=el;
			el.psl_ui=this;
			this._el_err=el.querySelector('span.error');
			this._el_input=el.querySelector('input,textarea');
			this._el_input.addEventListener('focus',e=>{
				this.error='';
			});
			this._el_input.addEventListener('input',e=>{
				this.error='';
			});
			if(this._el_input.constructor===HTMLTextAreaElement){
				if(this._el_input.hasAttribute('autoresize')){
					this._autoresize=e=>{
						this._el_input.style.height='0px';
						this._el_input.style.height=`calc(${16.89*Math.max(1,Math.ceil((this._el_input.scrollHeight-(37.09-16.89))/16.89))}px + 1.4rem + 2px)`;
					};
					this._el_input.addEventListener('change',this._autoresize);
					this._el_input.addEventListener('input',this._autoresize);
				}
				if(this._el_input.hasAttribute('oneline')){
					const listener=e=>{
						let filtered=this._el_input.value.replace(/\r?\n/gi, '');
						if(filtered===this._el_input.value) return;
						const {selectionStart,selectionEnd} = this._el_input;
						const delta=this._el_input.value.length-filtered.length;
						this._el_input.value=filtered;
						this._el_input.selectionStart=selectionStart-delta;
						this._el_input.selectionEnd=selectionEnd-delta;
					}
					this._el_input.addEventListener('change',listener,true);
					this._el_input.addEventListener('input',listener,true);
				}
				if(this._el_input.hasAttribute('tabs')){
					const listener=e=>{
						if (e.keyCode === 9){
							e.preventDefault();
							const start=this._el_input.selectionStart;
							this._el_input.value = `${this._el_input.value.substring(0, start)}\t${this._el_input.value.substring(this._el_input.selectionEnd)}`;
							this._el_input.selectionStart = this._el_input.selectionEnd = start + 1;
						}
					}
					this._el_input.addEventListener('keydown',listener,true);
				}
			}
		}
		get value(){
			return this._el_input.value||'';
		}
		set value(value){
			if(value.constructor!==String && value.constructor!==Number) value=JSON.stringify(value, null, "\t");
			this._el_input.value=value;
			if(this._autoresize) this._autoresize();
		}
		get error(){
			return this._el_err.innerHTML;
		}
		set error(value){
			if(value){
				this._el.classList.add('error');
				this._el_err.innerHTML=value;
			} else if(this.error){
				this._el.classList.remove('error');
				this._el_err.innerHTML='';
			}
		}
		set locked(f){
			this._locked=f;
			this._el.classList[f?'add':'remove']('disabled');
			this._el_input[`${f?'set':'remove'}Attribute`]('disabled','disabled');
		}
		get locked(){
			return this._locked;
		}
		set readonly(f){
			this._el_input.readOnly=!!f;
		}
		get readonly(){
			return this._el_input.readOnly;
		}
		validate(config){
			let valid=true;
			config.forEach(rule=>{
				if(!valid) return;
				if(rule.constructor===Function){
					try{
						rule(this.value);
					} catch(e){
						this.error=e;
						valid=false;
					}
				} else {
					switch(rule.type){
						case 'required':
							if(this.value===''){
								this.error=rule.msg;
								valid=false;
							}
						break;
					}
				}
			});
			return valid;
		}
	},
	form:class{
		constructor(rendered,config={}){ //el - rendered template
			if(rendered.constructor===Function) rendered=rendered();
			this._rendered=rendered;
			this._el_msg=this._rendered.el.querySelector('fieldset > p');
			if(!this._el_msg){
				this._el_msg=document.createElement('p');
				let fieldset=this._rendered.el.querySelector('fieldset');
				fieldset.insertBefore(this._el_msg,fieldset.firstChild);
			}
			this._el_submit=this._rendered.el.querySelector('button[type="submit"]');
			this._rendered.el.addEventListener('submit',e=>{
				e.stopPropagation();
				e.preventDefault();
				setImmediate(async ()=>{
					if(!(await this.validate())) return;
					this.locked=true;
					try{
						await this._submit(this.data);
					} catch(err){
						if(err.field&&err.msg) this.errors=[{field:err.field,msg:err.msg}];
					}
					this.locked=false;
				});
				return false;
			});
			this._validate_config=config.validate||{};
			this._submit=config.submit||(async()=>{});
			this._lock=config.lock;
			this._fields=new Map();
			let els=Array.from(this._rendered.el.querySelectorAll('[data-psl_ui_form_name]'));
			for(let el of els) if(_classes[el.dataset.psl_ui]) this._fields.set(el.dataset.psl_ui_form_name,new _classes[el.dataset.psl_ui](el));
		}
		get view(){
			return this._rendered.el;
		}
		set view(v){}
		get rpoints(){
			return this._rendered.rpoints;
		}
		set rpoints(v){}
		get data(){
			let data={};
			for(let [name,field] of this._fields) data[name]=field.value;
			return data;
		}
		set data(v){
			Object.entries(v).forEach(([name,value])=>{
				if(!this._fields.has(name)) return;
				this._fields.get(name).value=value;
			});
		}
		get fields(){
			return this._fields;
		}
		set fields(_){}
		set message(v){
			this._el_msg.innerHTML=v||'';
		}
		get message(){
			return this._el_msg.innerHTML;
		}
		set errors(arr){
			this._errors=arr;
			for(let field of this._fields.values()) field.error='';
			this._errors.forEach(({field,msg})=>{
				if(!this._fields.has(field)) return;
				this._fields.get(field).error=msg;
			});
		}
		get errors(){
			return this._errors;
		}
		_errors_set(){

		}
		set locked(f){
			this._locked=f;
			this._rendered.el.classList[f?'add':'remove']('disabled');
			this._lock?this._lock(f):_loaders[f?'show':'hide'](this._rendered.el);
			this._el_submit[`${f?'set':'remove'}Attribute`]("disabled","disabled");
			for(let field of this._fields.values()) field.locked=f;
		}
		get locked(){
			return this._locked;
		}
		validate(){
			let valid=true;
			for(let [field,config] of Object.entries(this._validate_config)){
				valid=this._fields.get(field).validate(config)&&valid;
			}
			return valid;
		}
	},
	el:class{
		constructor(config){
			config.model=config.model||{};
			this._view=_render_template(config.view,config.model);
			this.model=config.model;
		}
		_keydown(el){
			el.addEventListener('keydown',e=>{
				if(!this.editable) return;
				switch(e.keyCode){
					case 13:
						this.save();
					break;
					case 27:
						this.editable=false;
					break;
					default:
						return;
					break;
				}
				e.stopPropagation();
				e.preventDefault();
				return false;
			});
		}
		_blur(el,clb){
			el.addEventListener('blur',clb/*e=>{
				el.psl_bluring=setTimeout(clb,100);
			}*/);
		}
		_selection(el){
			setTimeout(()=>{
				el.focus();
				if(el.nodeName==='INPUT') return el.click();
				let range = document.createRange();
				range.selectNodeContents(el);
				range.collapse(false);
				let sel = window.getSelection();
				sel.removeAllRanges();
				sel.addRange(range);
			},0);
		}
		get model(){
			return this._model;
		}
		set model(model){
			this._model=model;
			this.render();
		}
		get view(){
			return this._view.el;
		}
		set editable(value){
			if(this._editable===value) return;
			this._editable=value;
			this._view.el.classList[value?'add':'remove']('editable');
		}
		get editable(){
			return this._editable;
		}
		render(){

		}
	}
}

const _loaders=(function(){
	//TODO set timeout for re-rendering after appending div.
	//TODO transitionend in show and hide is nice, but doesn't work.
	class Loader{
		constructor(el){
			this._view=document.createElement('div');
			this._view.className='psl_ui_loader hidden';
			this._view.innerHTML='<div class="spinner"><svg viewBox="0 0 48 48"><circle cx="24" cy="24" r="23" fill="none" stroke-width="2" stroke-miterlimit="10"></circle></svg></div>';
			this.append(el);
		}
		async show({label="",force,transparent}){
			if(force) return this._show_action(label);
			await this.ready;
			if(transparent) this._view.classList.add('transparent');
			this._view.classList.remove('hidden');
			await wait(150);
			this._view.dataset.label=label;
			if(!label) this._view.removeAttribute('data-label');
		}
		_show_action(label){
			this._view.classList.remove('hidden');
			this._view.dataset.label=label;
			if(!label) this._view.removeAttribute('data-label');
		}
		async hide(){
			await this.ready;
			this._view.classList.add('hidden');
			await wait(150);
		}
		append(el){
			if(!el) return;
			this._view.classList[el.clientHeight<48?'add':'remove']('small');
			el.appendChild(this._view);
			this.ready=wait(1);
			this.pos_overwritten=false;
			if(window.getComputedStyle(el).position!=='static') return;
			this.pos_overwritten=true;
			this.pos_old_style=el.style.position;
			el.style.position='relative';
		}
		remove(){
			if(!this._view.parentNode) return;
			if(this.pos_overwritten) this._view.parentNode.style.position=this.pos_old_style;
			this._view.parentNode.removeChild(this._view);
		}
	};

	const _manager=new Map();
	const _unused=[];

	function _new(el){
		if(!_unused.length) return new Loader(el);
		let loader=_unused.pop();
		loader.append(el);
		return loader;
	}

	async function _show(el,config={}){
		if(!_manager.has(el)) _manager.set(el,_new(el));
		await _manager.get(el).show(config);
	}

	async function _hide(el){
		if(!_manager.has(el)) return;
		let instance=_manager.get(el);
		await instance.hide();
		instance.remove();
		_unused.push(instance);
		_manager.delete(el);
	}

	const _ext={
		show:_show,
		hide:_hide,
	}

	return _ext;
})();

const _dropdown=(()=>{
	let _shown;

	window.addEventListener('click',e=>{
		if(!_shown) return;
		if(!_DOM.parent(e.target,el=>el===_shown.el)) _hide();
	},true);

	async function _show(el,list){
		if(_shown) await _hide();
		_shown=_render_template('psl_ui.menu',{list:list});
		el.appendChild(_shown.el);
		await wait(1);
		_shown.el.classList.remove('hidden');
		await wait(150);
		_shown.events=new psl_events.class();
		
		return _shown;
	}
	
	async function _hide(){
		if(!_shown || !_shown.el.parentNode){
			_shown=undefined;
			return;
		}
		_shown.el.classList.add('hidden');
		await wait(150);
		if(!_shown) return;
		_shown.el.parentNode.removeChild(_shown.el);
		_shown.events.emit('hide');
		_shown=undefined;
	}

	const _ext={
		show:_show,
		hide:_hide
	};

	return _ext;
})();

const _modal=(()=>{

	const _bkg=document.createElement('div');
	_bkg.classList.add('psl_ui_modal_bkg');
	_bkg.classList.add('hidden');
	document.body.appendChild(_bkg);

	psl_actions.add('psl_ui.modal.close',()=>{
		if(_shown) _hide();
	});

	let _shown;

	async function _show({title,body,actions}){
		if(_shown) await _hide();
		let data={
			cls:[],
			header:true,
			title:title||"",
			body:body.constructor===String?body:'',
			footer:true,
		};
		if(!data.title){
			data.header=false;
			data.cls.push('headerless');
		}
		if(actions===false){
			data.footer=false;
			data.cls.push('footerless');
		} else {
			data.actions=actions||[
				{cls:'',action:'psl_ui.modal.close',label:'OK'}
			];
		}
		if(data.cls.length){
			data.cls=' '+data.cls.join(' ');
		} else {
			data.cls='';
		}
		let _act_uids=[];
		if(data.actions) data.actions.forEach(act=>{
			if(typeof(act.action)==='string') return;
			window.crypto.getRandomValues(_uid);
			let uid='psl_ui.modal.action.'+_uid[0]+'_'+_uid[1];
			_act_uids.push(uid);
			psl_actions.add(uid,act.action);
			act.action=uid;
		});
		_shown=_render_template('psl_ui.modal',data);
		_shown.actions=_act_uids;
		document.body.appendChild(_shown.el);
		if(body.constructor!==String){
			let arr;
			if(body.constructor===Array){
				arr=body;
			} else if(body instanceof HTMLElement){
				_shown.rpoints.body.appendChild(body);
			} else if(body.els){
				arr=body.els;
			} else if(body.view){
				_shown.rpoints.body.appendChild(body.view);
			}
			if(arr) arr.forEach(el=>_shown.rpoints.body.appendChild(el));
		}
		await wait(1);
		_shown.el.classList.remove('hidden');
		_bkg.classList.remove('hidden');
		if(document.body.scrollHeight>document.body.offsetHeight){
			document.body.classList.add('psl_ui_modal_shown');
		}
		await wait(150);
		
		return _shown.rpoints.content;
	}

	async function _hide(){
		if(_shown && _shown.actions) psl_actions.remove(_shown.actions);
		if(!_shown || !_shown.el.parentNode){
			_shown=undefined;
			return;
		}
		_shown.el.classList.add('hidden');
		_bkg.classList.add('hidden');
		document.body.classList.remove('psl_ui_modal_shown');
		await wait(150);
		if(!_shown) return;
		_shown.el.parentNode.removeChild(_shown.el);
		_shown=undefined;
	}
	
	async function _confirm({title,body,confirm,cancel}){
		return _show({
			title:title,
			body:body,
			actions:[
				{
					label:confirm.label,
					action:confirm.action
				},
				{
					label:cancel.constructor===String?cancel:cancel.label,
					action:(cancel.constructor!==String?cancel.action:false)||'psl_ui.modal.close'
				}
			]
		})
	}
	
	function _form({title,form}){
		return _show({
			title,
			body:form,
			actions:false
		});
	}

	const _ext={
		show:_show,
		hide:_hide,
		confirm:_confirm,
		form:_form
	};

	return _ext;
})();

const _DOM=(function(){

	function _parent(el,filter=()=>true){
		while(el&&!filter(el)) el=el.parentNode;
		return el;
	}
	
	function _controller(el,name='controller'){
		while(el&&!el[name]) el=el.parentNode;
		return el[name];
	}

	function _children(el,filter=()=>true){
		return Array.from(el.childNodes).filter(filter);
	}

	function _text(el,text){
// 			el.innerHTML=text;
		if(el.psl_ui_dom_text) return el.psl_ui_dom_text.textContent=text;
		el.psl_ui_dom_text=_children(el,el=>el.nodeName==='#text')[0];
		if(!el.psl_ui_dom_text){
			el.psl_ui_dom_text=document.createTextNode('');
			el.appendChild(el.psl_ui_dom_text);
		}
		el.psl_ui_dom_text.textContent=text;
	}

	function _replace(source,target){
		source.parentNode.insertBefore(target,source);
		source.parentNode.removeChild(source);
	}
	
	function _clear(el){
		while(el.firstChild) el.removeChild(el.firstChild);
	}
	
	const _render_container=document.createElement('div');
	function _str2html(str){
		_render_container.innerHTML=str;
		let els=Array.from(_render_container.childNodes);
		for(let i=els.length;i--;) if(els[i].nodeName==='#text') els.splice(i,1);
		if(els.length===1) return els[0];
		return els;
	}
	function _str2text(str){
		_render_container.innerHTML=str;
		return _render_container.innerText.replace(/\s\s/g,' ').replace(/\>/g,'&gt;').replace(/\</g,'&lt;');
	}
	
	function _content(el,content){
		_clear(el);
		
		if(content.constructor===String){
			el.innerHTML=content;
		} else {
			_clear(el);
			
			let arr;
			if(content.constructor===Array){
				arr=content;
			} else if(content instanceof HTMLElement){
				arr=[content];
			} else if(content.els){
				arr=content.els;
			} else if(content.view){
				arr=[content.view];
			}
			arr.forEach(arr_el=>{
				if(arr_el instanceof HTMLElement){
					el.appendChild(arr_el);
				} else if(arr_el.constructor===String){
					const rendered=_str2html(arr_el);
					if(rendered.constructor===Array){
						rendered.forEach(arr_el=>el.appendChild(arr_el));
					} else {
						el.appendChild(rendered);
					}
				} else {
					console.error('Unknown type',arr_el,el);
					debugger;
				}
			});
		}
	}

	const _ext={
		parent:_parent,
		controller:_controller,
		children:_children,
		text:_text,
		replace:_replace,
		clear:_clear,
		str2html:_str2html,
		str2text:_str2text,
		content:_content,
	}

	return _ext;
})();

export default {
	helpers:{
		add:_add_helper
	},
	templates:{
		add:_add_template,
		render:_render_template,
		rpoints:_get_rpoints,
		insert:_insert_template,
	},
	loader:_loaders,
	dropdown:_dropdown,
	modal:_modal,
	classes:_classes,
	DOM:_DOM
}
